import { METRICS_EVENTS } from "@cede/types";
import { API_ENDPOINT, QueryKeys } from "@cede/utils";
import { useApi } from "@/hooks/core/useApi";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useMutation, useQueryClient } from "@tanstack/react-query";

interface UseSpinningWheelSpinOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Hook to handle spinning wheel spin API call
 * Initiates spins with the specified number of tickets
 * Returns 204 No Content - actual results are delivered via SSE notifications
 */
export const useSpinningWheelSpin = ({ onSuccess, onError }: UseSpinningWheelSpinOptions = {}) => {
  const { hubUser, auth } = useHubUser();
  const apiHelper = useApi();
  const queryClient = useQueryClient();
  const { trackEvent } = useMetricsEvents();

  const spinMutation = useMutation({
    mutationFn: async (ticketCount: number): Promise<void> => {
      if (!hubUser?.id) {
        throw new Error("User not authenticated");
      }

      // Track when user starts spinning the wheel
      trackEvent(METRICS_EVENTS.SPINNING_WHEEL_SPIN_STARTED, {
        ticketCount,
        hubUserId: hubUser.id,
      });

      // Endpoint returns 204 No Content
      // Results will be delivered via SSE notifications
      await apiHelper.postAuth(auth, API_ENDPOINT.SPINNING_WHEEL_SPIN, {
        bodyParams: { ticketCount },
      });
    },
    onSuccess: () => {
      // Invalidate tickets query to refresh count
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.SPINNING_WHEEL_TICKETS],
      });

      onSuccess?.();
    },
    onError: (error) => {
      // Track when spinning wheel encounters an error
      trackEvent(METRICS_EVENTS.SPINNING_WHEEL_SPIN_ERROR, {
        error: error instanceof Error ? error.message : "Unknown error",
        hubUserId: hubUser?.id,
      });

      onError?.(error as Error);
    },
  });

  return {
    spin: spinMutation.mutate,
    isSpinning: spinMutation.isPending,
    error: spinMutation.error?.message ?? null,
    reset: spinMutation.reset,
  };
};
